import { useEffect, useState } from "react";
import { X, Gift, Sparkles } from "lucide-react";

const STORAGE_KEY = "flonte_exit_intent_shown_v1";
const PENDING_COUPON_KEY = "flonte_pending_coupon_v1";
const COUPON_CODE = "WELCOME10";

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Show only once per session / localStorage; skip if already redeemed
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch { /* ignore */ }

    let shown = false;
    let mobileTimer = null;

    const markShown = () => {
      if (shown) return;
      shown = true;
      try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
      setOpen(true);
    };

    // Desktop: detect cursor leaving viewport from the top
    const onMouseLeave = (e) => {
      if (e.clientY <= 10) markShown();
    };

    // Mobile: 30s dwell on any page as a soft exit-intent proxy
    const isTouch = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);
    if (isTouch) {
      mobileTimer = setTimeout(markShown, 30000);
    } else {
      document.addEventListener("mouseout", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseout", onMouseLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []);

  const copyCode = () => {
    try {
      navigator.clipboard.writeText(COUPON_CODE);
      // Also stage pending coupon so cart auto-applies
      window.localStorage.setItem(PENDING_COUPON_KEY, COUPON_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-ink-900/55 backdrop-blur-sm grid place-items-center p-4 animate-[fadeIn_0.3s_ease-out]"
      data-testid="exit-intent-popup"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-cream-50 via-blush-50 to-cream-100 rounded-[2rem] border border-blush-200 shadow-2xl p-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-blush-200/45 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blush-300/40 blur-3xl" />

        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/80 grid place-items-center text-ink-700 hover:text-blush-600 hover:bg-white transition"
        >
          <X size={16} strokeWidth={1.6} />
        </button>

        <div className="relative text-center">
          <div className="inline-flex h-14 w-14 rounded-full bg-white shadow-sm border border-blush-200 items-center justify-center text-blush-600 mb-4">
            <Gift size={22} strokeWidth={1.5} />
          </div>
          <span className="font-script text-3xl text-blush-500">wait, lovely</span>
          <h2 className="font-heading text-3xl text-ink-900 mt-1 leading-tight">
            Here's 10% off your first order
          </h2>
          <p className="mt-3 font-body text-sm text-ink-700">
            A quiet welcome to Flownté. Use this code at checkout on any order above ₹1,000.
          </p>

          <button
            onClick={copyCode}
            data-testid="exit-intent-copy-btn"
            className="mt-6 w-full rounded-2xl border-2 border-dashed border-blush-400 bg-white/85 backdrop-blur px-6 py-4 flex items-center justify-between gap-3 hover:bg-white transition group"
          >
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-ink-500 font-body">Your code</p>
              <p className="font-heading text-2xl text-blush-600 tracking-[0.18em]">{COUPON_CODE}</p>
            </div>
            <span className="text-xs uppercase tracking-[0.2em] font-body text-blush-700 group-hover:text-blush-900">
              {copied ? "✓ Copied!" : "Tap to copy"}
            </span>
          </button>

          <p className="mt-4 text-[11px] font-body text-ink-500 inline-flex items-center gap-1.5">
            <Sparkles size={11} strokeWidth={1.5} className="text-blush-600" />
            Auto-applied when you reach the cart
          </p>
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
